import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Component, Input, OnChanges, SimpleChanges, Inject } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormArray, Validators, AbstractControl } from '@angular/forms';
import {
    AddCaseRequirementRequest,
    CaseRequirementResponse,
    UWRequirementTableData,
    RequirementResponse,
    UWBaseProps,
    NoteResponse,
} from '@Underwriting/models';

import { UwwUtils } from '../../utils';
import { UWRequirementsDataService, UWWorkbenchService } from '@Underwriting/services';
import { CaseRequirementStatuses } from '@Underwriting/types';
import { SubSink } from 'subsink';
import * as _ from 'lodash';
import UpdateCaseRequirementMatchRequest from '@CaseSummary/underwriting/models/uww-update-case-requirement-match-request';

@Component({
    selector: 'uw-requirements-add-form-v2',
    templateUrl: './uw-requirements-add-form-v2.component.html',
    styleUrls: ['./uw-requirements-add-form-v2.component.scss']
})

export default class UWRequirementsAddFormV2 {
    @Input() baseProps: UWBaseProps;
    @Input() caseRequirements: CaseRequirementResponse[];
    @Input() requirements: RequirementResponse[] = [];

    public caseRequirementForm: UntypedFormGroup;
    searchText: string = '';
    characterLimit = 32;
    notes: NoteResponse[] = [];
    notesLoaded = false;
    requirementsToDisable: UWRequirementTableData[] = [];

    subs = new SubSink();

    constructor(
        private uwRequirementsDataService: UWRequirementsDataService,
        private _fb: UntypedFormBuilder,
        private underWriterWorkbenchService: UWWorkbenchService,
        public addRequirementDialogRef: MatDialogRef<UWRequirementsAddFormV2>,
        @Inject(MAT_DIALOG_DATA) public data: any
    ) { }

    //#region private helpers

    private initializeRequirements() {
        this.subscribeToNotes();
        this.loadRequirements();
    }

    private subscribeToNotes() {
        this.subs.add(this.uwRequirementsDataService.loadNotes(this.baseProps.client.code, this.baseProps.case.id).subscribe({
            next: notes => {
                this.notes = notes;
                this.notesLoaded = true;
                this.loadRequirements();
            },
        }));
    }

    loadRequirements() {
        const { integrationRequirements } = this.baseProps;
        this.requirementsToDisable = UwwUtils.mapRequirementsToTableData(integrationRequirements, this.caseRequirements, this.requirements, this.notes, this.baseProps.caseIntegrationQueueEntries);
    }

    trackById(index: number, group: AbstractControl) {
        return group.get('id')?.value;
    }

    createItem(requirement: RequirementResponse, isDisabled = false): UntypedFormGroup {
        const initiallySelected = isDisabled;

        const group = this._fb.group({
            id: [requirement.id, Validators.required],
            name: [requirement.name, [Validators.required]],
            subtitle: ['', Validators.maxLength(this.characterLimit)],
            isSelected: [isDisabled],
            initiallySelected: [initiallySelected],
        });

        if (isDisabled) {
            group.disable();
        }

        return group;
    }

    get caseRequirementsFormArray(): UntypedFormArray {
        return this.caseRequirementForm.get('caseRequirements') as UntypedFormArray;
    }

    get filteredCaseRequirements(): AbstractControl[] {
        const term = this.searchText?.toLowerCase() ?? '';
        return this.caseRequirementsFormArray.controls.filter((group: AbstractControl) => {
            const name = group.get('name')?.value?.toLowerCase() || '';
            return name.includes(term);
        })
        .sort((a, b) => {
            const nameA = a.get('name')?.value?.toLowerCase() || '';
            const nameB = b.get('name')?.value?.toLowerCase() || '';
            return nameA.localeCompare(nameB);
        });
    }


    saveSelectedRequirement() {

        const selectedRequirements = this.caseRequirementsFormArray.controls
            .filter(req => {
                const isSelected = req.get('isSelected')?.value;
                const isDisabled = req.disabled;
                return isSelected && !isDisabled;
            })
            .map(req => req.value);

        if (selectedRequirements.length === 0) {
            return;
        }

        const addCaseRequirements: AddCaseRequirementRequest[] = [];
        const updateCaseRequirements: UpdateCaseRequirementMatchRequest[] = [];
        for (const req of selectedRequirements) {
            
            const currentIntegrationId = this.requirements.find(x => x.id === req.id)?.integrationId || null;
            const currentIntegrationActionId = this.requirements.find(x => x.id === req.id)?.integrationActionId || null;
            
            const match = this.caseRequirements.find(x => (x.integration) && x.integration.id == currentIntegrationId && x.integration.actionId == currentIntegrationActionId);
            if (match) {
                const updateCaseRequirement: UpdateCaseRequirementMatchRequest = {
                    caseRequirementId: match.id,
                    requirementId: req.id,
                    name: req.name,
                    subtitle: req.subtitle
                };

                updateCaseRequirements.push(updateCaseRequirement);
            }
            else {
                const addCaseRequirement: AddCaseRequirementRequest = {
                    caseId: this.baseProps.case.id,
                    requirementId: req.id,
                    name: req.name,
                    subtitle: req.subtitle,
                    status: CaseRequirementStatuses.Unknown.toString()
                };

                addCaseRequirements.push(addCaseRequirement);
            }
        }

        if (addCaseRequirements.length > 0) {
            this.underWriterWorkbenchService
                .addMultipleCaseRequirement(this.baseProps.account.id, addCaseRequirements, this.baseProps.case.id)
                .subscribe(() => {
                });
        }

        if (updateCaseRequirements.length > 0) {
            this.underWriterWorkbenchService
                .updateMultipleCaseRequirementMatch(updateCaseRequirements, this.baseProps.account.id)
                .subscribe(() => {
                });
        }
    }

    cancel() {
        this.addRequirementDialogRef.close();
    }

    saveAndClose() {
        this.saveSelectedRequirement();
        this.addRequirementDialogRef.close();
    }

    //#endregion

    //#region Lifecycle Hooks
    ngOnInit() {
        this.baseProps = this.data.baseProps;
        this.caseRequirements = this.data.caseRequirements;
        this.requirements = this.data.requirements || [];

        this.initializeRequirements();

        this.caseRequirementForm = this._fb.group({
            caseRequirements: this._fb.array([])
        })

        const control = <UntypedFormArray>this.caseRequirementForm.get('caseRequirements');
        
        // Do not add requirements that are already disabled.
        const matchedRequirementIds = this.requirementsToDisable
                                        .map(disableItem => {
                                            const match = this.caseRequirements.find(req => req.id === disableItem.caseRequirementId);
                                            return match ? match.requirementId : null;
                                        })
                                        .filter(id => id !== null);

        for (const requirement of this.requirementsToDisable) {
            const req: RequirementResponse = {
                id: requirement.caseRequirementId,
                name: requirement.name,
                clientId: this.baseProps.client.id,
                accountId: this.baseProps.account.id,
                sortOrder: 0,
                sectionId: ''
            }
            control.push(this.createItem(req, true));
        }
        for (const requirement of this.requirements) {
            if (!matchedRequirementIds.includes(requirement.id)) {
                control.push(this.createItem(requirement, false));
            }
        }
    }

    ngOnDestroy() {
        this.subs.unsubscribe();
    }
    //#endregion
}
